import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

interface Movie {
  id: number;
  title: string;
  description: string;
  thumbnail: string;
  video_url?: string;
  video_url_en?: string;
  video_url_ru?: string;
  available_languages?: string;
  genre: string;
  release_year: number;
  duration: number;
  rating: number;
  is_premium: number;
  status: string;
  view_count: number;
  trailer_url?: string;
}

interface Pagination {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

const API_URL = `${window.location.origin}/api`;

const GENRES = [
  'Action', 'Comedy', 'Drama', 'Horror', 'Romance', 'Sci-Fi', 
  'Thriller', 'Mystery', 'Adventure', 'Animation', 'Crime', 
  'Documentary', 'Family', 'Fantasy', 'War', 'Western'
];

const AdminMovies: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [movies, setMovies] = useState<Movie[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showThumbnailModal, setShowThumbnailModal] = useState(false);
  
  // Form state
  const [editingMovie, setEditingMovie] = useState<Movie | null>(null);
  const [deleteMovieId, setDeleteMovieId] = useState<number | null>(null);
  const [deleteMovieTitle, setDeleteMovieTitle] = useState('');
  
  // Thumbnail quick upload
  const [thumbnailMovieId, setThumbnailMovieId] = useState<number | null>(null);
  const [thumbnailMovieTitle, setThumbnailMovieTitle] = useState('');
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  
  // Message state
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' | 'info' } | null>(null);

  // Check admin access
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      navigate('/login');
      return;
    }
    
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload.role !== 'admin') {
        navigate('/');
        return;
      }
    } catch (error) {
      localStorage.removeItem('auth_token');
      navigate('/login');
    }
  }, [navigate]);

  const showNotification = (text: string, type: 'success' | 'error' | 'info') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 3000);
  };

  const loadMovies = useCallback(async (page: number = 1) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/admin/movies?page=${page}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error(`API error: ${response.status}`);
      
      const data = await response.json();
      if (data.status !== 'success') throw new Error(data.message);
      
      setMovies(data.data.data || []);
      setPagination(data.data.pagination || null);
      setCurrentPage(page);
    } catch (error: any) {
      showNotification(error.message || 'Failed to load movies', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMovies(1);
  }, [loadMovies]);

  // Get movie by ID for editing
  const fetchMovieDetails = async (id: number) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/admin/movies/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error(`API error: ${response.status}`);
      
      const data = await response.json();
      if (data.status !== 'success') throw new Error(data.message);
      
      return data.data;
    } catch (error: any) {
      showNotification(error.message || 'Failed to fetch movie', 'error');
      return null;
    }
  };

  // Open edit modal
  const handleEdit = async (id: number) => {
    const movie = await fetchMovieDetails(id);
    if (movie) {
      setEditingMovie(movie);
      setShowEditModal(true);
    }
  };

  // Delete movie
  const handleDelete = (id: number, title: string) => {
    setDeleteMovieId(id);
    setDeleteMovieTitle(title);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteMovieId) return;
    
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/admin/movies/${deleteMovieId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      if (data.status !== 'success') throw new Error(data.message);
      
      showNotification('Movie deleted successfully', 'success');
      setShowDeleteModal(false);
      loadMovies(currentPage);
    } catch (error: any) {
      showNotification(error.message || 'Failed to delete movie', 'error');
    }
  };

  // Quick thumbnail upload
  const openThumbnailModal = (id: number, title: string) => {
    setThumbnailMovieId(id);
    setThumbnailMovieTitle(title);
    setThumbnailUrl('');
    setShowThumbnailModal(true);
  };

  const saveThumbnail = async () => {
    if (!thumbnailMovieId || !thumbnailUrl) {
      showNotification('Please enter a thumbnail URL', 'error');
      return;
    }
    
    try {
      const token = localStorage.getItem('auth_token');
      const formData = new FormData();
      formData.append('change_thumbnail', '1');
      formData.append('thumbnail_url', thumbnailUrl);
      
      const response = await fetch(`${API_URL}/admin/movies/${thumbnailMovieId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      const data = await response.json();
      if (data.status !== 'success') throw new Error(data.message);
      
      showNotification('Thumbnail updated successfully', 'success');
      setShowThumbnailModal(false);
      loadMovies(currentPage);
    } catch (error: any) {
      showNotification(error.message || 'Failed to update thumbnail', 'error');
    }
  };

  // Get thumbnail URL
  const getThumbnailUrl = (movie: Movie) => {
    if (movie.thumbnail?.startsWith('http')) return movie.thumbnail;
    if (movie.thumbnail) return `/public/uploads/thumbnails/${movie.thumbnail}`;
    return `https://via.placeholder.com/50x75?text=${encodeURIComponent(movie.title.slice(0, 10))}`;
  };

  return (
    <div className="min-h-screen bg-netflix-darker pt-20 px-4 md:px-8">
      {/* Notification */}
      {message && (
        <div className={`fixed top-24 right-4 z-50 px-6 py-3 rounded-lg shadow-lg ${
          message.type === 'success' ? 'bg-green-600' : 
          message.type === 'error' ? 'bg-red-600' : 'bg-blue-600'
        } text-white`}>
          {message.text}
        </div>
      )}

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Manage Movies</h1>
          <div className="flex gap-4">
            <button 
              onClick={() => navigate('/admin')}
              className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
            >
              Back to Dashboard
            </button>
            <button 
              onClick={() => setShowAddModal(true)}
              className="px-6 py-2 bg-netflix-red hover:bg-red-700 rounded-lg transition-colors flex items-center gap-2"
            >
              <i className="fas fa-plus"></i> Add New Movie
            </button>
          </div>
        </div>

        {/* Movies Table */}
        <div className="bg-white/5 rounded-xl border border-white/10 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-netflix-red/20">
                <tr>
                  <th className="px-4 py-4 text-left text-sm font-semibold text-netflix-red uppercase">ID</th>
                  <th className="px-4 py-4 text-left text-sm font-semibold text-netflix-red uppercase">Thumbnail</th>
                  <th className="px-4 py-4 text-left text-sm font-semibold text-netflix-red uppercase">Title</th>
                  <th className="px-4 py-4 text-left text-sm font-semibold text-netflix-red uppercase">Genre</th>
                  <th className="px-4 py-4 text-left text-sm font-semibold text-netflix-red uppercase">Year</th>
                  <th className="px-4 py-4 text-left text-sm font-semibold text-netflix-red uppercase">Videos</th>
                  <th className="px-4 py-4 text-left text-sm font-semibold text-netflix-red uppercase">Premium</th>
                  <th className="px-4 py-4 text-left text-sm font-semibold text-netflix-red uppercase">Views</th>
                  <th className="px-4 py-4 text-left text-sm font-semibold text-netflix-red uppercase">Status</th>
                  <th className="px-4 py-4 text-left text-sm font-semibold text-netflix-red uppercase">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={10} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-netflix-red mb-2"></div>
                        <span className="text-gray-400">Loading movies...</span>
                      </div>
                    </td>
                  </tr>
                ) : movies.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="px-6 py-12 text-center text-gray-400">
                      No movies found
                    </td>
                  </tr>
                ) : (
                  movies.map(movie => {
                    const hasEnglish = !!(movie.video_url_en || movie.video_url);
                    const hasRussian = !!movie.video_url_ru;
                    
                    return (
                      <tr key={movie.id} className="border-t border-white/5 hover:bg-white/5 transition-colors">
                        <td className="px-4 py-4">{movie.id}</td>
                        <td className="px-4 py-4">
                          <div 
                            className="relative cursor-pointer group"
                            onClick={() => openThumbnailModal(movie.id, movie.title)}
                          >
                            <img 
                              src={getThumbnailUrl(movie)}
                              alt={movie.title}
                              className="w-[50px] h-[75px] object-cover rounded"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.src = `https://via.placeholder.com/50x75?text=${encodeURIComponent(movie.title.slice(0, 5))}`;
                              }}
                            />
                            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center rounded transition-opacity">
                              <i className="fas fa-camera text-white"></i>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4 font-medium">{movie.title}</td>
                        <td className="px-4 py-4 text-gray-300">{movie.genre}</td>
                        <td className="px-4 py-4">{movie.release_year}</td>
                        <td className="px-4 py-4">
                          <div className="flex gap-1">
                            <span 
                              title={`English ${hasEnglish ? 'Uploaded' : 'Not Uploaded'}`}
                              className={`inline-flex items-center justify-center w-6 h-6 rounded text-[10px] font-bold text-white ${hasEnglish ? 'bg-green-500' : 'bg-gray-600'}`}
                            >
                              EN
                            </span>
                            <span 
                              title={`Russian ${hasRussian ? 'Uploaded' : 'Not Uploaded'}`}
                              className={`inline-flex items-center justify-center w-6 h-6 rounded text-[10px] font-bold text-white ${hasRussian ? 'bg-green-500' : 'bg-gray-600'}`}
                            >
                              RU
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          {movie.is_premium ? (
                            <span className="text-yellow-400">✓</span>
                          ) : (
                            <span className="text-gray-500">✗</span>
                          )}
                        </td>
                        <td className="px-4 py-4 text-gray-300">{movie.view_count || 0}</td>
                        <td className="px-4 py-4">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            movie.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                          }`}>
                            {movie.status}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex gap-2">
                            <button 
                              onClick={() => handleEdit(movie.id)}
                              className="px-3 py-1 bg-white/10 hover:bg-white/20 rounded text-sm transition-colors"
                            >
                              Edit
                            </button>
                            <button 
                              onClick={() => handleDelete(movie.id, movie.title)}
                              className="px-3 py-1 bg-red-600 hover:bg-red-700 rounded text-sm transition-colors"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {pagination && pagination.last_page > 1 && (
          <div className="flex justify-center items-center gap-4 mt-6">
            {currentPage > 1 && (
              <button 
                onClick={() => loadMovies(currentPage - 1)}
                className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded transition-colors"
              >
                Previous
              </button>
            )}
            <span className="text-gray-400">
              Page {currentPage} of {pagination.last_page}
            </span>
            {currentPage < pagination.last_page && (
              <button 
                onClick={() => loadMovies(currentPage + 1)}
                className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded transition-colors"
              >
                Next
              </button>
            )}
          </div>
        )}
      </div>

      {/* Add Movie Modal */}
      <AddMovieModal 
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={() => {
          setShowAddModal(false);
          loadMovies(currentPage);
          showNotification('Movie added successfully', 'success');
        }}
        onError={(msg) => showNotification(msg, 'error')}
      />

      {/* Edit Movie Modal */}
      <EditMovieModal
        isOpen={showEditModal}
        movie={editingMovie}
        onClose={() => {
          setShowEditModal(false);
          setEditingMovie(null);
        }}
        onSuccess={() => {
          setShowEditModal(false);
          setEditingMovie(null);
          loadMovies(currentPage);
          showNotification('Movie updated successfully', 'success');
        }}
        onError={(msg) => showNotification(msg, 'error')}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
          <div className="bg-netflix-dark rounded-xl max-w-md w-full border border-white/10">
            <div className="p-6 border-b border-white/10">
              <h2 className="text-xl font-bold">Delete Movie</h2>
            </div>
            <div className="p-6">
              <p className="text-gray-300 mb-4">
                Are you sure you want to delete "<span className="text-white font-medium">{deleteMovieTitle}</span>"?
              </p>
              <p className="text-red-400 text-sm">This action cannot be undone.</p>
            </div>
            <div className="p-6 border-t border-white/10 flex justify-end gap-4">
              <button 
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded transition-colors"
              >
                Cancel
              </button>
              <button 
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quick Thumbnail Modal */}
      {showThumbnailModal && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
          <div className="bg-netflix-dark rounded-xl max-w-md w-full border border-white/10">
            <div className="p-6 border-b border-white/10 flex justify-between items-center">
              <h2 className="text-xl font-bold">
                <i className="fas fa-image text-netflix-red mr-2"></i>
                Upload Thumbnail
              </h2>
              <button onClick={() => setShowThumbnailModal(false)} className="text-gray-400 hover:text-white text-2xl">&times;</button>
            </div>
            <div className="p-6">
              <p className="text-gray-400 mb-4">
                Movie: <strong className="text-white">{thumbnailMovieTitle}</strong>
              </p>
              <div className="mb-4">
                <label className="block text-gray-300 font-medium mb-2">Thumbnail URL</label>
                <input
                  type="url"
                  value={thumbnailUrl}
                  onChange={(e) => setThumbnailUrl(e.target.value)}
                  placeholder="https://..."
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-netflix-red text-white"
                />
              </div>
              {thumbnailUrl && (
                <div className="mb-4">
                  <img 
                    src={thumbnailUrl} 
                    alt="Preview" 
                    className="max-w-[150px] rounded border-2 border-green-500"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                    }}
                  />
                </div>
              )}
            </div>
            <div className="p-6 border-t border-white/10 flex justify-end gap-4">
              <button 
                onClick={() => setShowThumbnailModal(false)}
                className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded transition-colors"
              >
                Cancel
              </button>
              <button 
                onClick={saveThumbnail}
                className="px-4 py-2 bg-netflix-red hover:bg-red-700 rounded transition-colors"
              >
                <i className="fas fa-save mr-2"></i>Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Add Movie Modal Component
interface AddMovieModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onError: (msg: string) => void;
}

const AddMovieModal: React.FC<AddMovieModalProps> = ({ isOpen, onClose, onSuccess, onError }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    genre: '',
    release_year: new Date().getFullYear(),
    duration: 120,
    rating: 7.0,
    is_premium: 0,
    video_url_en: '',
    video_url_ru: '',
    trailer_url: '',
    thumbnail_url: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const token = localStorage.getItem('auth_token');
      const formDataObj = new FormData();
      
      Object.entries(formData).forEach(([key, value]) => {
        formDataObj.append(key, String(value));
      });
      
      // Set available languages based on video URLs
      const languages = [];
      if (formData.video_url_en) languages.push('en');
      if (formData.video_url_ru) languages.push('ru');
      formDataObj.append('available_languages', languages.join(','));
      
      // Set main video_url for backward compatibility
      if (formData.video_url_en) {
        formDataObj.append('video_url', formData.video_url_en);
      }
      formDataObj.append('video_source', 'url');
      
      const response = await fetch(`${API_URL}/admin/movies`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formDataObj
      });
      
      const data = await response.json();
      if (data.status !== 'success') throw new Error(data.message);
      
      onSuccess();
      setFormData({
        title: '',
        description: '',
        genre: '',
        release_year: new Date().getFullYear(),
        duration: 120,
        rating: 7.0,
        is_premium: 0,
        video_url_en: '',
        video_url_ru: '',
        trailer_url: '',
        thumbnail_url: ''
      });
    } catch (error: any) {
      onError(error.message || 'Failed to add movie');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/95 z-50 overflow-y-auto">
      <div className="min-h-screen w-full bg-netflix-darker">
        <div className="flex justify-between items-center p-6 border-b border-white/10 sticky top-0 bg-netflix-darker z-10">
          <h2 className="text-2xl font-bold">Add New Movie</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-netflix-red text-3xl px-4 py-2 hover:bg-white/10 rounded-lg transition-all">&times;</button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 max-w-4xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-4">
              <div>
                <label className="block text-gray-300 font-semibold mb-2">Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-netflix-red text-white"
                />
              </div>
              
              <div>
                <label className="block text-gray-300 font-semibold mb-2">Description *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  rows={4}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-netflix-red text-white resize-vertical"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-300 font-semibold mb-2">Genre *</label>
                  <select
                    value={formData.genre}
                    onChange={(e) => setFormData({ ...formData, genre: e.target.value })}
                    required
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-netflix-red text-white"
                  >
                    <option value="">Select Genre</option>
                    {GENRES.map(g => <option key={g} value={g}>{g}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-gray-300 font-semibold mb-2">Year *</label>
                  <input
                    type="number"
                    value={formData.release_year}
                    onChange={(e) => setFormData({ ...formData, release_year: parseInt(e.target.value) })}
                    required
                    min={1900}
                    max={2099}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-netflix-red text-white"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-300 font-semibold mb-2">Duration (min)</label>
                  <input
                    type="number"
                    value={formData.duration}
                    onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) })}
                    min={1}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-netflix-red text-white"
                  />
                </div>
                <div>
                  <label className="block text-gray-300 font-semibold mb-2">Rating (0-10)</label>
                  <input
                    type="number"
                    value={formData.rating}
                    onChange={(e) => setFormData({ ...formData, rating: parseFloat(e.target.value) })}
                    min={0}
                    max={10}
                    step={0.1}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-netflix-red text-white"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-gray-300 font-semibold mb-2">Premium</label>
                <select
                  value={formData.is_premium}
                  onChange={(e) => setFormData({ ...formData, is_premium: parseInt(e.target.value) })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-netflix-red text-white"
                >
                  <option value={0}>Free</option>
                  <option value={1}>Premium</option>
                </select>
              </div>
            </div>
            
            {/* Right Column */}
            <div className="space-y-4">
              <div>
                <label className="block text-gray-300 font-semibold mb-2">Thumbnail URL</label>
                <input
                  type="url"
                  value={formData.thumbnail_url}
                  onChange={(e) => setFormData({ ...formData, thumbnail_url: e.target.value })}
                  placeholder="https://..."
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-netflix-red text-white"
                />
              </div>
              
              <div>
                <label className="block text-gray-300 font-semibold mb-2">Trailer URL (YouTube)</label>
                <input
                  type="url"
                  value={formData.trailer_url}
                  onChange={(e) => setFormData({ ...formData, trailer_url: e.target.value })}
                  placeholder="https://www.youtube.com/watch?v=..."
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-netflix-red text-white"
                />
              </div>
              
              {/* Language Video URLs */}
              <div className="p-4 bg-netflix-red/10 border-2 border-netflix-red/30 rounded-xl mt-6">
                <label className="block text-white font-bold mb-3 text-lg">
                  🎬 Video Language Versions
                </label>
                
                {/* English Video */}
                <div className="mb-4 p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-white font-semibold">🇬🇧 English Version *</label>
                    {formData.video_url_en && (
                      <span className="px-2 py-1 bg-green-600 text-white text-xs rounded-full font-bold">✓ Set</span>
                    )}
                  </div>
                  <input
                    type="url"
                    value={formData.video_url_en}
                    onChange={(e) => setFormData({ ...formData, video_url_en: e.target.value })}
                    placeholder="Bunny CDN URL (playlist.m3u8)"
                    required
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-green-500 text-white font-mono text-sm"
                  />
                  <small className="text-gray-400 block mt-1">Enter the Bunny.net CDN video URL for English version</small>
                </div>
                
                {/* Russian Video */}
                <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-white font-semibold">
                      🇷🇺 Russian Version <span className="text-gray-400 font-normal text-sm">(Optional)</span>
                    </label>
                    {formData.video_url_ru && (
                      <span className="px-2 py-1 bg-blue-600 text-white text-xs rounded-full font-bold">✓ Set</span>
                    )}
                  </div>
                  <input
                    type="url"
                    value={formData.video_url_ru}
                    onChange={(e) => setFormData({ ...formData, video_url_ru: e.target.value })}
                    placeholder="Bunny CDN URL (playlist.m3u8)"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-blue-500 text-white font-mono text-sm"
                  />
                  <small className="text-gray-400 block mt-1">Enter the Bunny.net CDN video URL for Russian version (optional)</small>
                </div>
                
                <div className="mt-4 p-3 bg-black/30 rounded-lg border-l-4 border-netflix-red">
                  <small className="text-gray-300 block leading-relaxed">
                    💡 <strong>Bunny.net:</strong> Upload videos to Bunny.net first, then paste the CDN URLs here.<br/>
                    🎬 <strong>Player:</strong> When both languages are uploaded, viewers can switch between EN/RU.
                  </small>
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex gap-4 mt-8 pt-6 border-t border-white/10">
            <button 
              type="submit" 
              disabled={submitting}
              className="flex-1 px-6 py-4 bg-netflix-red hover:bg-red-700 rounded-lg font-semibold transition-all text-lg disabled:opacity-50"
            >
              {submitting ? (
                <><span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"></span> Adding...</>
              ) : (
                <><i className="fas fa-plus mr-2"></i> Add Movie</>
              )}
            </button>
            <button 
              type="button" 
              onClick={onClose}
              className="px-8 py-4 bg-white/10 hover:bg-white/20 rounded-lg font-semibold transition-all text-lg"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit Movie Modal Component
interface EditMovieModalProps {
  isOpen: boolean;
  movie: Movie | null;
  onClose: () => void;
  onSuccess: () => void;
  onError: (msg: string) => void;
}

const EditMovieModal: React.FC<EditMovieModalProps> = ({ isOpen, movie, onClose, onSuccess, onError }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    genre: '',
    release_year: new Date().getFullYear(),
    duration: 120,
    rating: 7.0,
    is_premium: 0,
    status: 'active',
    video_url_en: '',
    video_url_ru: '',
    trailer_url: '',
    thumbnail_url: '',
    change_thumbnail: false
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (movie) {
      setFormData({
        title: movie.title || '',
        description: movie.description || '',
        genre: movie.genre || '',
        release_year: movie.release_year || new Date().getFullYear(),
        duration: movie.duration || 120,
        rating: movie.rating || 7.0,
        is_premium: movie.is_premium || 0,
        status: movie.status || 'active',
        video_url_en: movie.video_url_en || movie.video_url || '',
        video_url_ru: movie.video_url_ru || '',
        trailer_url: movie.trailer_url || '',
        thumbnail_url: '',
        change_thumbnail: false
      });
    }
  }, [movie]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!movie) return;
    
    setSubmitting(true);
    
    try {
      const token = localStorage.getItem('auth_token');
      const formDataObj = new FormData();
      
      formDataObj.append('title', formData.title);
      formDataObj.append('description', formData.description);
      formDataObj.append('genre', formData.genre);
      formDataObj.append('release_year', String(formData.release_year));
      formDataObj.append('duration', String(formData.duration));
      formDataObj.append('rating', String(formData.rating));
      formDataObj.append('is_premium', String(formData.is_premium));
      formDataObj.append('status', formData.status);
      formDataObj.append('video_url_en', formData.video_url_en);
      formDataObj.append('video_url_ru', formData.video_url_ru);
      formDataObj.append('trailer_url', formData.trailer_url);
      
      // Set available languages
      const languages = [];
      if (formData.video_url_en) languages.push('en');
      if (formData.video_url_ru) languages.push('ru');
      formDataObj.append('available_languages', languages.join(','));
      
      // Set main video_url for backward compatibility
      if (formData.video_url_en) {
        formDataObj.append('video_url', formData.video_url_en);
      }
      
      // Handle thumbnail
      if (formData.change_thumbnail && formData.thumbnail_url) {
        formDataObj.append('change_thumbnail', '1');
        formDataObj.append('thumbnail_url', formData.thumbnail_url);
      }
      
      const response = await fetch(`${API_URL}/admin/movies/${movie.id}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formDataObj
      });
      
      const data = await response.json();
      if (data.status !== 'success') throw new Error(data.message);
      
      onSuccess();
    } catch (error: any) {
      onError(error.message || 'Failed to update movie');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen || !movie) return null;

  // Get current thumbnail URL
  const currentThumbnail = movie.thumbnail?.startsWith('http') 
    ? movie.thumbnail 
    : movie.thumbnail 
      ? `/public/uploads/thumbnails/${movie.thumbnail}`
      : null;

  return (
    <div className="fixed inset-0 bg-black/95 z-50 overflow-y-auto">
      <div className="min-h-screen w-full bg-netflix-darker">
        <div className="flex justify-between items-center p-6 border-b border-white/10 sticky top-0 bg-netflix-darker z-10">
          <h2 className="text-2xl font-bold">Edit Movie</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-netflix-red text-3xl px-4 py-2 hover:bg-white/10 rounded-lg transition-all">&times;</button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 max-w-4xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-4">
              <div>
                <label className="block text-gray-300 font-semibold mb-2">Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-netflix-red text-white"
                />
              </div>
              
              <div>
                <label className="block text-gray-300 font-semibold mb-2">Description *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  rows={4}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-netflix-red text-white resize-vertical"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-300 font-semibold mb-2">Genre *</label>
                  <select
                    value={formData.genre}
                    onChange={(e) => setFormData({ ...formData, genre: e.target.value })}
                    required
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-netflix-red text-white"
                  >
                    <option value="">Select Genre</option>
                    {GENRES.map(g => <option key={g} value={g}>{g}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-gray-300 font-semibold mb-2">Year *</label>
                  <input
                    type="number"
                    value={formData.release_year}
                    onChange={(e) => setFormData({ ...formData, release_year: parseInt(e.target.value) })}
                    required
                    min={1900}
                    max={2099}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-netflix-red text-white"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-300 font-semibold mb-2">Duration (min)</label>
                  <input
                    type="number"
                    value={formData.duration}
                    onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) })}
                    min={1}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-netflix-red text-white"
                  />
                </div>
                <div>
                  <label className="block text-gray-300 font-semibold mb-2">Rating (0-10)</label>
                  <input
                    type="number"
                    value={formData.rating}
                    onChange={(e) => setFormData({ ...formData, rating: parseFloat(e.target.value) })}
                    min={0}
                    max={10}
                    step={0.1}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-netflix-red text-white"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-300 font-semibold mb-2">Premium</label>
                  <select
                    value={formData.is_premium}
                    onChange={(e) => setFormData({ ...formData, is_premium: parseInt(e.target.value) })}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-netflix-red text-white"
                  >
                    <option value={0}>Free</option>
                    <option value={1}>Premium</option>
                  </select>
                </div>
                <div>
                  <label className="block text-gray-300 font-semibold mb-2">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-netflix-red text-white"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
            </div>
            
            {/* Right Column */}
            <div className="space-y-4">
              {/* Current Thumbnail */}
              <div>
                <label className="block text-gray-300 font-semibold mb-2">Current Thumbnail</label>
                {currentThumbnail && (
                  <img 
                    src={currentThumbnail} 
                    alt={movie.title}
                    className="max-w-[200px] rounded-lg border border-white/20 mb-2"
                  />
                )}
                <label className="flex items-center gap-2 cursor-pointer mt-2">
                  <input
                    type="checkbox"
                    checked={formData.change_thumbnail}
                    onChange={(e) => setFormData({ ...formData, change_thumbnail: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <span className="text-gray-300">Replace Thumbnail</span>
                </label>
              </div>
              
              {formData.change_thumbnail && (
                <div>
                  <label className="block text-gray-300 font-semibold mb-2">New Thumbnail URL</label>
                  <input
                    type="url"
                    value={formData.thumbnail_url}
                    onChange={(e) => setFormData({ ...formData, thumbnail_url: e.target.value })}
                    placeholder="https://..."
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-netflix-red text-white"
                  />
                  {formData.thumbnail_url && (
                    <img 
                      src={formData.thumbnail_url} 
                      alt="Preview"
                      className="max-w-[150px] rounded-lg border-2 border-green-500 mt-2"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                  )}
                </div>
              )}
              
              <div>
                <label className="block text-gray-300 font-semibold mb-2">Trailer URL (YouTube)</label>
                <input
                  type="url"
                  value={formData.trailer_url}
                  onChange={(e) => setFormData({ ...formData, trailer_url: e.target.value })}
                  placeholder="https://www.youtube.com/watch?v=..."
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-netflix-red text-white"
                />
              </div>
              
              {/* Language Video URLs */}
              <div className="p-4 bg-netflix-red/10 border-2 border-netflix-red/30 rounded-xl mt-6">
                <label className="block text-white font-bold mb-3 text-lg">
                  🎬 Video Language Versions
                </label>
                
                {/* English Video */}
                <div className="mb-4 p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-white font-semibold">🇬🇧 English Version</label>
                    {formData.video_url_en && (
                      <span className="px-2 py-1 bg-green-600 text-white text-xs rounded-full font-bold">✓ EN Uploaded</span>
                    )}
                  </div>
                  <input
                    type="url"
                    value={formData.video_url_en}
                    onChange={(e) => setFormData({ ...formData, video_url_en: e.target.value })}
                    placeholder="Bunny CDN URL (playlist.m3u8)"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-green-500 text-white font-mono text-sm"
                  />
                </div>
                
                {/* Russian Video */}
                <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-white font-semibold">
                      🇷🇺 Russian Version <span className="text-gray-400 font-normal text-sm">(Optional)</span>
                    </label>
                    {formData.video_url_ru ? (
                      <span className="px-2 py-1 bg-blue-600 text-white text-xs rounded-full font-bold">✓ RU Uploaded</span>
                    ) : (
                      <span className="px-2 py-1 bg-white/20 text-gray-400 text-xs rounded-full font-bold">⊗ Not uploaded</span>
                    )}
                  </div>
                  <input
                    type="url"
                    value={formData.video_url_ru}
                    onChange={(e) => setFormData({ ...formData, video_url_ru: e.target.value })}
                    placeholder="Bunny CDN URL (playlist.m3u8)"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-blue-500 text-white font-mono text-sm"
                  />
                </div>
                
                <div className="mt-4 p-3 bg-black/30 rounded-lg border-l-4 border-netflix-red">
                  <small className="text-gray-300 block leading-relaxed">
                    💡 <strong>Upload to Bunny.net:</strong> Paste URLs directly.<br/>
                    🎬 <strong>Player:</strong> When both languages uploaded, viewers can switch between EN/RU.
                  </small>
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex gap-4 mt-8 pt-6 border-t border-white/10">
            <button 
              type="submit" 
              disabled={submitting}
              className="flex-1 px-6 py-4 bg-netflix-red hover:bg-red-700 rounded-lg font-semibold transition-all text-lg disabled:opacity-50"
            >
              {submitting ? (
                <><span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"></span> Updating...</>
              ) : (
                <><i className="fas fa-save mr-2"></i> Update Movie</>
              )}
            </button>
            <button 
              type="button" 
              onClick={onClose}
              className="px-8 py-4 bg-white/10 hover:bg-white/20 rounded-lg font-semibold transition-all text-lg"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminMovies;
